import React, { useState, useEffect, Fragment, useRef } from "react";
import "./App.css";
import AppRoutes from "./routes";
import { langObj } from "./locale/index";
import { LangCtx, PayLoadCtx, RoleCtx, SignalerCtx, CustomCtx, SpecializationsCtx } from "./lib/context";
import Loader from "./lib/ui/loader/Loader";
import { ThemeProvider } from "@mui/material/styles";
import { createTheme } from "@mui/material/styles";
import { ToastContainer } from "react-toastify";
import ConsultSignaler from "./lib/consultSignaler";
import "react-toastify/dist/ReactToastify.css";
import { currentsession, currentuserinfo, getSpecializationList, getDomainData } from "./actions/users";
import { Auth } from "@aws-amplify/auth";
import Amplify from "@aws-amplify/core";
import SessionExpired from "./containers/common/SessionExpired";
import SessionExpiredModal from "./containers/common/SessionExpiredModal";
import ConnectionLost from "./containers/common/ConnectionLost";
import { getAxios } from "./actions/appointment";
import { useDispatch } from "react-redux";
import { currencyRates } from "./lib/ReduxStore";
import axios from "axios";
import { Country } from "country-state-city";
// import psl from 'psl';
import { getSubdomain } from "./lib/domain";
import { setupAxiosInterceptor, logoutAndRedirect } from "./lib/axiosInterceptor";

const theme = createTheme({
    palette: {
        primary: { main: "#24a4c4" },
        secondary: {
            main: "#2b80c4",
        },
    },
});

function App() {
    const [l, setLang] = useState(langObj[process.env.REACT_APP_defaultLang]);
    const [payload, setPayload] = useState(null);
    const [role, setRole] = useState(null);
    const [loading, setLoading] = useState(true);
    const [signaler, setSignaler] = useState(null);
    const [custom, setCustom] = useState({
        check: false,
        logo: process.env.REACT_APP_logo,
        whiteLable: false,
        subDomain: process.env.REACT_APP_defaultdomian,
        defaultClinicId: process.env.REACT_APP_defaultClinicId,
        clientstripekey: process.env.REACT_APP_clientstripekey,
        serverstripekey: process.env.REACT_APP_serverstripekey,
        name: process.env.REACT_APP_defaultClinicName,
    });
    const [socket_status, setSocketStatus] = useState("INIT");
    const [internet_status, setNetStatus] = useState(window.navigator.onLine);
    const [specializationsList, setSpecializationsList] = useState(null);
    const [currencyCode, setCurrencyCode] = useState(localStorage.getItem("currencyCode"));
    const [showSessionExpiredModal, setShowSessionExpiredModal] = useState(false);
    const dispatch = useDispatch();
    // const _domain = psl.parse(window.location.hostname);
    const _domain = getSubdomain(window.location.hostname);

    // Refs to prevent duplicate API calls
    const specializationsFetched = useRef(false);
    const sessionFetched = useRef(false);
    const currencyFetched = useRef(new Map());

    const awsconfig = (domainConfig) => {
        // SecureID migration: Amplify is used only for S3 Storage now.
        // Cognito Auth and the API gateway endpoints are obsolete — all
        // backend calls go through axios/OffLineAPI with SecureID tokens.
        // Storage config (region, bucket, identityPoolId) always comes from
        // env on the client — it does not vary by subdomain/clinic.
        Amplify.configure({
            Storage: {
                region: process.env.REACT_APP_s3Region,
                bucket: process.env.REACT_APP_s3,
                identityPoolId: process.env.REACT_APP_IDENTITY_POOL_ID,
            },
        });

        setCustom({
            check: true,
            logo: domainConfig?.logo ? domainConfig.logo : process.env.REACT_APP_logo,
            whiteLable: true,
            subDomain: domainConfig?.subdomain ? domainConfig.subdomain : process.env.REACT_APP_defaultdomian,
            defaultClinicId: domainConfig ? (domainConfig.clinicId ? domainConfig.clinicId : null) : process.env.REACT_APP_defaultClinicId,
            tenantId: domainConfig?.tenantId ? domainConfig.tenantId : null,
            name: domainConfig?.name ? domainConfig.name : process.env.REACT_APP_defaultClinicName,
        });
    };
    const loadLanguage = () => {
        const lang = localStorage.getItem("lang");
        const defaultLang = process.env.REACT_APP_defaultLang;
        const selectedLang = lang && lang !== defaultLang ? langObj[lang] : langObj[defaultLang];
        setLang(selectedLang);
    };

    const configureDomain = async () => {
        const domainMismatch = window.location.hostname !== "localhost" && _domain?.subdomain !== process.env.REACT_APP_defaultdomian;
        if (domainMismatch) {
            try {
                const res = await getDomainData(_domain.subdomain);

                const tenant = res?.data ?? res;
                if (tenant && tenant.tenantId) {
                    localStorage.setItem("tenantId", String(tenant.tenantId));
                    awsconfig(tenant);
                } else {
                    redirectToNotReachedPage();
                }
            } catch (err) {
                redirectToNotReachedPage();
            }
        } else {
            awsconfig(null);
        }
    };

    const redirectToNotReachedPage = () => {
        window.location.href = "https://app.homeo.care/sitenotreached";
    };

    const handleNetworkStatus = (isOnline) => {
        setNetStatus(isOnline);
        if (isOnline) {
            window.location.reload();
        }
    };

    const handleSessionExpiration = () => {
        setShowSessionExpiredModal(true);
    };

    const handleSessionExpiredConfirm = async () => {
        setShowSessionExpiredModal(false);
        await logoutAndRedirect();
    };

    useEffect(() => {
        loadLanguage();
        setLoading(true);

        // Setup axios interceptor for session expiration handling
        setupAxiosInterceptor(handleSessionExpiration);

        configureDomain();

        window.addEventListener("offline", () => handleNetworkStatus(false));
        window.addEventListener("online", () => handleNetworkStatus(true));

        return () => {
            window.removeEventListener("offline", () => handleNetworkStatus(false));
            window.removeEventListener("online", () => handleNetworkStatus(true));
        };
    }, []);

    useEffect(() => {
        // Only fetch specializations once when custom.check becomes true
        if (custom?.check && !specializationsFetched.current) {
            specializationsFetched.current = true;
            getDropdownData();
        }

        if (custom?.check && !sessionFetched.current) {
            sessionFetched.current = true;
            const sidToken = localStorage.getItem("secureid_access_token");
            if (!sidToken) {
                setLoading(false);
            } else {
                (async () => {
                    try {
                        const meRes = await currentuserinfo();
                        const dbUser = meRes?.attributes || meRes?.data?.data || meRes?.data || meRes?.attributes || null;
                        const consultRole = dbUser?.consultRole;
                        let _role = null;
                        if (consultRole === "doctor") _role = "D";
                        else if (consultRole === "patient") _role = "P";
                        else if (consultRole === "clinic") _role = "C";
                        else if (consultRole === "clinicAdmin") _role = "CA";
                        else if (consultRole === "superAdmin") _role = "SA";
                        else _role = "P";

                        const dbUserId = dbUser?.userId;
                        if (dbUserId && !localStorage.getItem("sub")) {
                            localStorage.setItem("sub", dbUserId);
                        }
                        setRole(_role);
                        getPayLoad();
                    } catch (err) {
                        console.error("Error restoring SecureID session:", err);
                        // Token invalid/expired — clear and force re-login.
                        localStorage.removeItem("secureid_access_token");
                        localStorage.removeItem("secureid_id_token");
                        localStorage.removeItem("token");
                        localStorage.removeItem("tenantId");
                        setLoading(false);
                    }
                })();
            }
        }
    }, [custom?.check]);

    useEffect(() => {
        if (l?.locale) {
            document.body.classList.toggle("rtl", l.locale === "ar");
        }
    }, [l]);

    const connectSignaler = (res) => {
        if (!signaler) {
            const consultSignaler = new ConsultSignaler(l);
            if (res?.data?.patientId) {
                consultSignaler.init(res.data.patientId, "P");
                setSignaler(() => consultSignaler);
            } else {
                console.error("Invalid response data:", res);
            }
        }
    };
    useEffect(() => {
        const initializeSignaler = () => {
            const consultSignaler = new ConsultSignaler(l);
            consultSignaler.init(payload.userId, role);
            setSignaler(consultSignaler);
        };

        if (payload?.userId && role) {
            if (role === "SA" || role === "CA") {
                setSignaler(null);
                setSocketStatus("CONN");
            } else if (!signaler) {
                initializeSignaler();
            }
        } else if (signaler) {
            signaler.closeSocket();
            setSignaler(null);
        }

        return () => {
            if (signaler && !payload && !role) {
                signaler.closeSocket();
                setSignaler(null);
            }
        };
    }, [payload, role]);

    useEffect(() => {
        if (signaler) {
            const handleDisconnected = () => setSocketStatus("DISCONN");
            const handleConnected = () => setSocketStatus("CONN");

            signaler.on("disconnected", handleDisconnected);
            signaler.on("connected", handleConnected);

            return () => {
                signaler.off("disconnected", handleDisconnected);
                signaler.off("connected", handleConnected);
            };
        }
    }, [signaler]);

    async function showPosition(position) {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        axios
            .get(`https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}`)
            .then((res) => {
                if (res?.data) {
                    if (res?.data?.address?.country_code !== "") {
                        let data = Country.getCountryByCode((res?.data?.address?.country_code).toUpperCase());
                        localStorage.setItem("currencyCode", data.currency);
                        localStorage.setItem("country", res.data.address.country);
                        setCurrencyCode(data.currency);
                    }
                }
            })
            .catch((err) => {
                console.log("err................:(", "err");
            });
    }

    useEffect(() => {
        const currentUrl = new URL(window.location.href); // Ensure it uses the current page's URL
        const queryParams = currentUrl.searchParams;
        let headless = queryParams.get("headless") === "true"; // Ensure `headless` is treated as a boolean
        if (navigator?.geolocation && !headless) {
            navigator.geolocation.getCurrentPosition(showPosition, (err) => console.log("error"));
        }
    }, []);

    const getPayLoad = async () => {
        try {
            setLoading(true);
            const res = await currentuserinfo();

            const tenantId = res?.data?.tenantId ?? res?.tenantId ?? res?.attributes?.tenantId ?? null;
            if (tenantId) localStorage.setItem("tenantId", String(tenantId));

            const userAttributes = res?.attributes;
            if (userAttributes?.userId) {
                setPayload(userAttributes);
                if (process.env.REACT_APP_isOffline !== "true") {
                    setSocketStatus("CONN");
                }
            } else {
                setPayload(null);
            }
        } catch (err) {
            console.error("Error in current userinfo:", err);
            setPayload(null);
        } finally {
            setLoading(false);
        }
    };

    const getDropdownData = async () => {
        await getSpecializationList()
            .then((res) => {
                if (res && res.data) {
                    setSpecializationsList(res.data);
                }
            })
            .catch((err) => {
                console.log("err in get specialization======", err);
            });
    };

    const getCurrencies = async () => {
        const curr = currencyCode ? currencyCode.toLowerCase() : "inr";

        // Prevent duplicate calls for same currency
        if (currencyFetched.current.get(curr)) {
            return;
        }
        currencyFetched.current.set(curr, true);

        try {
            getAxios(curr)
                .then((res) => {
                    // Guard against a failed/empty response (e.g. the currency API
                    // Network Error) — res.data may be undefined, so don't index blindly.
                    const result = res?.data?.[curr];
                    if (result) {
                        dispatch(currencyRates(result));
                    }
                })
                .catch((err) => console.log("err in currency rates ===", err));
        } catch (e) {
            console.log("eeeeee", e);
        }
    };

    useEffect(() => {
        getCurrencies();
    }, [currencyCode]);

    return (
        <ThemeProvider theme={theme}>
            <LangCtx.Provider value={{ l, setLang, currencyCode, setCurrencyCode }}>
                <PayLoadCtx.Provider value={{ payload, getPayLoad, setPayload }}>
                    <RoleCtx.Provider value={{ role, setRole }}>
                        <SignalerCtx.Provider value={{ signaler, connectSignaler }}>
                            <CustomCtx.Provider value={{ custom, setCustom }}>
                                <SpecializationsCtx.Provider value={{ specializationsList }}>
                                    <div className="App">
                                        {socket_status === "DISCONN" && <SessionExpired />}
                                        {!internet_status && <ConnectionLost />}
                                        {loading ? (
                                            <Loader />
                                        ) : (
                                            <Fragment>
                                                {socket_status === "INIT" && payload !== null && <Loader />}
                                                {socket_status !== "DISCONN" && <AppRoutes />}
                                            </Fragment>
                                        )}
                                        <SessionExpiredModal open={showSessionExpiredModal} onConfirm={handleSessionExpiredConfirm} />
                                        <ToastContainer />
                                    </div>
                                </SpecializationsCtx.Provider>
                            </CustomCtx.Provider>
                        </SignalerCtx.Provider>
                    </RoleCtx.Provider>
                </PayLoadCtx.Provider>
            </LangCtx.Provider>
        </ThemeProvider>
    );
}

export default App;
